/* eslint-disable no-restricted-properties */
/* 
  Create an array of values that range between a minimum and maximum value.

  Rather than a linear allocation of the values between the minimum and
  maximum, an exponential function is used.
    - the values start off increasing slowly
    - then ramp up faster
    - and near the end of the array, increase rapidly

  exponent
    A smaller value will result in a more gradual ramping up of values.
    A larger value will result in the values ramping up more quickly.
    A value of about 2 is likely a good starting point.
 */
export function createNonLinearSteps(
  min: number,
  max: number,
  stepCount: number,
  exponent: number
): number[] {
  // assign values using an exponential function
  let steps = new Array(stepCount);
  for (let i = 0; i < steps.length; i++) {
    steps[i] = Math.pow(i / stepCount, exponent) * (max - min) + min;
  }

  // round the numbers to nicer (more user friendly) values
  steps = steps.map(roundToSensibleNumber);

  // don't want adjusted values for min or max
  steps[0] = min;
  steps[stepCount - 1] = max;

  // remove any duplicates
  steps = steps.filter(
    (value, index) => index === steps.length - 1 || value !== steps[index + 1]
  );

  return steps;
}

/*
  A number is rounded to a reasonably user friendly number.

  The rounding is to the nearest 1, 2, or 5, or a multiple of a power
  of 10 of those numbers (10, 20, 50, 100, 200, 500, 1000, 2000...).

  See the accompanying unit tests for some examples.
 */
export function roundToSensibleNumber(value: number): number {
  // values less than 10 are simply rounded to an integer
  if (value < 10) {
    return Math.round(value);
  }

  // Scale the number to a normalised value between 10 and 100.
  //    23   -> 23
  //    786  -> 78
  //    4328 -> 43
  const previousPowerOf10 = Math.pow(10, Math.floor(Math.log10(value)) - 1);
  const scaledToLessThan100 = value / previousPowerOf10;

  // Round the 10 to 100 value, to the nearest 1, 2, or 5.
  //    small numbers, to the nearest 1
  //    medium numbers, to the nearest 2
  //    large numbers, to the nearest 5
  let roundToNearest;
  if (scaledToLessThan100 < 16) {
    // 10 -> 15
    roundToNearest = 1;
  } else if (scaledToLessThan100 < 30) {
    // 16 -> 29
    roundToNearest = 2;
  } else {
    // 30 -> 100
    roundToNearest = 5;
  }
  const rounded =
    Math.round((scaledToLessThan100 + roundToNearest) / roundToNearest) *
      roundToNearest -
    roundToNearest;

  // Multiply the rounded number back up to its original scale.
  return previousPowerOf10 * rounded;
}
